import { getToken, clearToken } from '../utils/authToken';
/**
 * ImageGalleryModal.jsx - Ventana de Galería de Imágenes para DamyxIA
 *
 * 🟥 CUADRO NEGRO DEFINITIVO 2026-02-26 — Lazy load + paginación + cache.
 *   • Modal abre INSTANTÁNEAMENTE (sin esperar fetch).
 *   • Carga inicial = 20 thumbnails (no 50 ni "todo").
 *   • Infinite scroll: al llegar al final del grid, carga la siguiente página.
 *   • Cache a nivel de módulo: si el usuario cierra y vuelve a abrir, los
 *     items previos persisten en memoria (no se re-fetcha la página 1).
 *   • Backend ya soporta `skip` + `limit` en /api/workspace/thumbnails.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Image, Layers, Download, Trash2, CheckSquare, Square, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

import { BACKEND_URL as API_URL } from '../config/api';
import { saveBlobWithPicker } from '../utils/downloadBlob';

const PAGE_SIZE = 20;

/* 🟥 Cache a nivel de módulo (sobrevive al desmonte del modal).
   Si el usuario cierra y vuelve a abrir, los items ya cargados se reusan
   instantáneamente — solo el scroll dispara nuevas páginas. */
const galleryCache = {
  items: [],
  nextSkip: 0,
  hasMore: true,
  loadedOnce: false,
};
function resetGalleryCache() {
  galleryCache.items = [];
  galleryCache.nextSkip = 0;
  galleryCache.hasMore = true;
  galleryCache.loadedOnce = false;
}
// Permite a otros componentes invalidar el cache (ej. tras eliminar imágenes
// desde otra vista). Se expone vía window event ya disparado por la app.
if (typeof window !== 'undefined') {
  window.addEventListener('damyxia:trash-updated', () => {
    // No vaciamos el cache (las eliminaciones desde aquí ya ajustan el estado
    // local); pero si otra vista borró imágenes, forzamos refetch al próximo
    // open marcando loadedOnce=false sin perder la lista visible actual.
    galleryCache.loadedOnce = false;
  });
}

const ImageGalleryModal = ({
  isOpen,
  onClose,
  onSelectImage,
  onImageDeleted,
  language: propLanguage
}) => {
  const { t: globalT, language: contextLanguage } = useLanguage();
  const language = propLanguage || contextLanguage;

  // 🟥 Estado inicial reutilizando el cache de módulo: si ya hay items
  // cargados de una apertura previa, los mostramos INMEDIATAMENTE.
  const [images, setImages] = useState(galleryCache.items);
  const [isLoading, setIsLoading] = useState(false);          // primer fetch
  const [isLoadingMore, setIsLoadingMore] = useState(false);  // páginas siguientes
  const [hasMore, setHasMore] = useState(galleryCache.hasMore);
  const [filter, setFilter] = useState('source'); // 'source' | 'variant'
  const [deletingId, setDeletingId] = useState(null);
  const [pendingDeleteImage, setPendingDeleteImage] = useState(null);

  // Refs para evitar dobles fetchs y para el observer
  const isFetchingRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const sentinelRef = useRef(null);

  // Seleccion multiple — disponible en ambas pestanas (Originales y Variantes)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Usar traducciones del contexto
  const t = (key) => globalT(`imageGallery.${key}`);

  /* 🟥 fetchPage — carga la siguiente página desde el backend.
     Usa skip/limit del endpoint /api/workspace/thumbnails. */
  const fetchPage = useCallback(async (skip) => {
    if (isFetchingRef.current) return null;
    isFetchingRef.current = true;
    try {
      const token = getToken();
      if (!token || token === 'guest') return null;
      const resp = await axios.get(`${API_URL}/api/workspace/thumbnails`, {
        params: { skip, limit: PAGE_SIZE },
      });
      const items = Array.isArray(resp.data?.items) ? resp.data.items : [];
      return items;
    } catch (err) {
      console.error('[Gallery] page load failed:', err);
      return null;
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  /* Carga inicial: solo en la PRIMERA apertura (o si el cache fue invalidado). */
  useEffect(() => {
    if (!isOpen) return;
    // Si ya tenemos items cacheados y no se invalidó, no re-fetchamos.
    if (galleryCache.loadedOnce && galleryCache.items.length > 0) {
      setImages(galleryCache.items);
      setHasMore(galleryCache.hasMore);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const items = await fetchPage(0);
      if (cancelled) return;
      if (items == null) {
        setIsLoading(false);
        return;
      }
      const more = items.length === PAGE_SIZE;
      galleryCache.items = items;
      galleryCache.nextSkip = items.length;
      galleryCache.hasMore = more;
      galleryCache.loadedOnce = true;
      setImages(items);
      setHasMore(more);
      setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [isOpen, fetchPage]);

  /* loadMore — carga la siguiente página al hacer scroll cerca del final. */
  const loadMore = useCallback(async () => {
    if (!galleryCache.hasMore || isFetchingRef.current) return;
    setIsLoadingMore(true);
    const items = await fetchPage(galleryCache.nextSkip);
    if (items == null) {
      setIsLoadingMore(false);
      return;
    }
    const more = items.length === PAGE_SIZE;
    galleryCache.items = [...galleryCache.items, ...items];
    galleryCache.nextSkip = galleryCache.items.length;
    galleryCache.hasMore = more;
    setImages([...galleryCache.items]);
    setHasMore(more);
    setIsLoadingMore(false);
  }, [fetchPage]);

  /* IntersectionObserver para infinite scroll. Cuando el sentinel entra al
     viewport del scroll container, llama a loadMore(). */
  useEffect(() => {
    if (!isOpen) return;
    const sentinel = sentinelRef.current;
    const container = scrollContainerRef.current;
    if (!sentinel || !container) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some(e => e.isIntersecting)) loadMore();
      },
      { root: container, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOpen, loadMore, hasMore]);

  // Filtrar imagenes
  const filteredImages = images.filter(img => {
    if (filter === 'source' && img.type !== 'IMAGE_SOURCE') return false;
    if (filter === 'variant' && img.type !== 'IMAGE_VARIANT') return false;
    return true;
  });

  // Descargar imagen
  const handleDownloadImage = async (e, image) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const token = getToken();
      const response = await axios.get(
        `${API_URL}/api/workspace/items/${image.id}/download`,
        {}
      );
      
      if (response.data.success && response.data.base64) {
        let b64 = response.data.base64;
        if (b64.startsWith('data:')) b64 = b64.split(',')[1];
        const byteChars = atob(b64);
        const byteArr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
        const blob = new Blob([byteArr], { type: 'image/png' });
        const filename = response.data.filename || `${image.display_name || 'imagen'}.png`;
        const result = await saveBlobWithPicker(blob, filename, 'image/png');
        if (result === 'fallback') {
          toast.success(language === 'es' ? 'Imagen descargada' : 'Image downloaded');
        } else {
          toast.success(
            language === 'es'
              ? 'Imagen abierta en nueva pestaña. Click derecho → "Guardar imagen como…" para elegir la carpeta.'
              : 'Image opened in a new tab. Right-click → "Save image as…" to choose the folder.',
            { duration: 7000 }
          );
        }
      }
    } catch (error) {
      // Fallback: descargar desde preview URL
      const previewUrl = getPreviewUrl(image);
      if (previewUrl) {
        try {
          const resp = await fetch(previewUrl);
          const blob = await resp.blob();
          const filename = `${image.display_name || 'imagen'}.png`;
          const result = await saveBlobWithPicker(blob, filename, 'image/png');
          if (result === 'fallback') {
            toast.success(language === 'es' ? 'Imagen descargada' : 'Image downloaded');
          } else {
            toast.success(
              language === 'es'
                ? 'Imagen abierta en nueva pestaña. Click derecho → "Guardar imagen como…" para elegir la carpeta.'
                : 'Image opened in a new tab. Right-click → "Save image as…" to choose the folder.',
              { duration: 7000 }
            );
          }
        } catch (_e) {
          toast.error(language === 'es' ? 'Error al descargar' : 'Download error');
        }
      } else {
        toast.error(language === 'es' ? 'Error al descargar' : 'Download error');
      }
    }
  };

  // Eliminar imagen — abre modal de confirmacion (no window.confirm que rompe el DOM)
  const handleDeleteImage = (e, image) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!image || !image.id) {
      console.error('No image ID provided');
      toast.error(language === 'es' ? 'Error: ID de imagen no válido' : 'Error: Invalid image ID');
      return;
    }

    setPendingDeleteImage(image);
  };

  // Ejecuta el borrado real tras confirmacion del modal
  const confirmDeleteImage = async () => {
    const image = pendingDeleteImage;
    const isSpanish = language === 'es';
    setPendingDeleteImage(null);

    if (!image || !image.id) return;

    setDeletingId(image.id);
    try {
      const token = getToken();

      if (!token) {
        throw new Error('No authentication token');
      }

      // Eliminar workspace item
      await axios.delete(`${API_URL}/api/workspace/items/${image.id}`, {
      });

      // Actualizar lista local + cache
      setImages(prev => {
        const next = prev.filter(img => img.id !== image.id);
        galleryCache.items = next;
        galleryCache.nextSkip = Math.max(0, galleryCache.nextSkip - 1);
        return next;
      });

      // Notificar al componente padre que la imagen fue borrada
      if (onImageDeleted) {
        onImageDeleted(image.id);
      }

      // Auto-refresh badge de la Papelera en Dashboard.
      window.dispatchEvent(new CustomEvent('damyxia:trash-updated'));

      toast.success(isSpanish ? 'Imagen eliminada' : 'Image deleted');
    } catch (error) {
      console.error('Error deleting image:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(isSpanish ? `Error al eliminar: ${errorMsg}` : `Error deleting: ${errorMsg}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Manejar selección de imagen
  const handleSelectImage = (image) => {
    if (onSelectImage) {
      onSelectImage(image);
    }
    onClose();
  };

  /* ─── Seleccion multiple ─── */
  const toggleSelect = (e, imageId) => {
    e?.preventDefault();
    e?.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  };

  // Al cambiar de pestana, limpiar seleccion para evitar borrar items ocultos
  useEffect(() => { setSelectedIds(new Set()); }, [filter]);
  // Tambien limpiar al cerrar/abrir el modal
  useEffect(() => { if (!isOpen) setSelectedIds(new Set()); }, [isOpen]);

  const handleSelectAllVisible = () => {
    const visibleIds = filteredImages.map(i => i.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(visibleIds));
  };

  const confirmBulkDelete = async () => {
    const isSpanish = language === 'es';
    const ids = Array.from(selectedIds);
    setPendingBulkDelete(false);
    if (ids.length === 0) return;

    setIsBulkDeleting(true);
    const token = getToken();
    if (!token) {
      setIsBulkDeleting(false);
      toast.error(isSpanish ? 'Sesion invalida' : 'Invalid session');
      return;
    }

    let ok = 0;
    let fail = 0;
    // Borramos en paralelo pero con concurrencia acotada
    const CONCURRENCY = 4;
    const queue = [...ids];
    const runOne = async () => {
      while (queue.length) {
        const id = queue.shift();
        try {
          await axios.delete(`${API_URL}/api/workspace/items/${id}`, {
          });
          ok++;
        } catch {
          fail++;
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, runOne));

    setImages(prev => {
      const next = prev.filter(img => !ids.includes(img.id));
      galleryCache.items = next;
      galleryCache.nextSkip = Math.max(0, galleryCache.nextSkip - ids.length);
      return next;
    });
    if (onImageDeleted) ids.forEach(id => onImageDeleted(id));
    setSelectedIds(new Set());
    setIsBulkDeleting(false);

    // Auto-refresh badge de la Papelera.
    window.dispatchEvent(new CustomEvent('damyxia:trash-updated'));

    if (fail === 0) {
      toast.success(isSpanish ? `${ok} imagen(es) eliminadas` : `${ok} image(s) deleted`);
    } else {
      toast.error(isSpanish ? `${ok} eliminadas, ${fail} fallaron` : `${ok} deleted, ${fail} failed`);
    }
  };

  // Obtener URL de preview - priorizar base64 si existe
  const getPreviewUrl = (image) => {
    // PRIORIDAD 1: Usar thumbnail pre-generado (más rápido)
    if (image.thumbnail) {
      return image.thumbnail;
    }
    // PRIORIDAD 2: Usar base64 si existe
    if (image.base64) {
      return image.base64.startsWith('data:') 
        ? image.base64 
        : `data:image/png;base64,${image.base64}`;
    }
    // PRIORIDAD 3: Usar endpoint de thumbnail si hay linked_image_id
    if (image.linked_image_id) {
      return `${API_URL}/api/workspace/thumbnail/${image.linked_image_id}`;
    }
    // FALLBACK: preview_url original
    const url = image.preview_url || image.image_url;
    if (!url) return null;
    return url.startsWith('/api') ? `${API_URL}${url}` : url;
  };

  // Obtener etiqueta de tipo
  const getTypeLabel = (type) => {
    switch (type) {
      case 'IMAGE_SOURCE': return t('imageSource');
      case 'IMAGE_VARIANT': return t('imageVariant');
      default: return type;
    }
  };

  // Obtener color de etiqueta
  const getTypeColor = (type) => {
    switch (type) {
      case 'IMAGE_SOURCE': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'IMAGE_VARIANT': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      default: return 'bg-zinc-100 text-zinc-700 dark:bg-zinc-900/30 dark:text-zinc-400';
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center"
    >
      {/* Overlay de fondo */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Ventana Modal */}
      <div className="relative w-full max-w-4xl max-h-[85vh] bg-white dark:bg-zinc-800 rounded-2xl shadow-2xl overflow-hidden m-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-zinc-200 dark:border-zinc-700 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex-shrink-0">
              <Image className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-base sm:text-lg font-semibold text-zinc-900 dark:text-white truncate">
              {t('title')}
            </h2>
            <span className="text-sm text-zinc-500 dark:text-zinc-400 flex-shrink-0">
              ({filteredImages.length})
            </span>
          </div>
          
          {/* Botón cerrar */}
          <button
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-zinc-600 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-xl transition-colors"
            data-testid="close-gallery-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Filtros — flex-wrap para que en mobile no se corte "Eliminar" */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 px-3 sm:px-6 py-3 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 flex-shrink-0">
          {/* Filtros por tipo */}
          <div className="flex items-center gap-1 bg-white dark:bg-zinc-700 rounded-lg p-1">
            <button
              onClick={() => setFilter('source')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filter === 'source'
                  ? 'bg-emerald-600 text-white'
                  : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-600'
              }`}
              data-testid="filter-originals-btn"
            >
              {t('originals')}
            </button>
            <button
              onClick={() => setFilter('variant')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filter === 'variant'
                  ? 'bg-purple-600 text-white'
                  : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-600'
              }`}
              data-testid="filter-variants-btn"
            >
              {t('variants')}
            </button>
          </div>

          {/* Seleccion multiple — Seleccionar todo / Eliminar seleccionadas */}
          {filteredImages.length > 0 && (
            <div className="flex items-center gap-2 sm:ml-auto">
              <button
                type="button"
                onClick={handleSelectAllVisible}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-md transition-colors"
                data-testid="gallery-select-all-btn"
                title={language === 'es' ? 'Seleccionar todo' : 'Select all'}
              >
                {filteredImages.every(i => selectedIds.has(i.id)) && filteredImages.length > 0 ? (
                  <CheckSquare className="w-4 h-4" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                {language === 'es' ? 'Todo' : 'All'}
              </button>
              <button
                type="button"
                onClick={() => setPendingBulkDelete(true)}
                disabled={selectedIds.size === 0 || isBulkDeleting}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  selectedIds.size === 0
                    ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-400 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-500'
                }`}
                data-testid="gallery-bulk-delete-btn"
                title={language === 'es' ? 'Eliminar seleccionadas' : 'Delete selected'}
              >
                <Trash2 className="w-3.5 h-3.5" />
                {language === 'es' ? 'Eliminar' : 'Delete'}
                {selectedIds.size > 0 && ` (${selectedIds.size})`}
              </button>
            </div>
          )}
        </div>
        
        {/* Contenido - Grid de imágenes con scroll vertical + infinite scroll. */}
        <div
          ref={scrollContainerRef}
          className="flex-1 min-h-0 p-3 sm:p-4"
          style={{ maxHeight: '450px', overflowY: 'auto' }}
          data-testid="gallery-scroll-container"
        >
          {/* 🟥 Spinner SOLO en primer fetch sin items cacheados. Si ya hay
              items en pantalla, mostramos el grid + spinner inline al final. */}
          {isLoading && images.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-zinc-500">{t('loadingImages')}</span>
              </div>
            </div>
          ) : filteredImages.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="p-4 bg-zinc-100 dark:bg-zinc-700/50 rounded-full mb-4">
                <Image className="w-12 h-12 text-zinc-300 dark:text-zinc-600" />
              </div>
              <h3 className="text-lg font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                {t('noImages')}
              </h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                {t('noImagesHint')}
              </p>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
              {filteredImages.map((image) => {
                const isSelected = selectedIds.has(image.id);
                const hasSelection = selectedIds.size > 0;
                return (
                <div
                  key={image.id}
                  className={`group relative overflow-hidden bg-zinc-100 dark:bg-zinc-700 border-2 transition-all cursor-pointer w-full aspect-square ${
                    isSelected
                      ? 'border-red-500 ring-2 ring-red-500/30'
                      : 'border-zinc-200 dark:border-zinc-600 hover:border-indigo-500'
                  }`}
                  style={{ borderRadius: '6px' }}
                  onClick={(e) => {
                    // Si hay una seleccion activa, el click normal alterna seleccion
                    if (hasSelection) { toggleSelect(e, image.id); return; }
                    handleSelectImage(image);
                  }}
                  data-testid={`gallery-image-${image.id}`}
                >
                  {/* Imagen con lazy loading */}
                  <img
                    src={getPreviewUrl(image)}
                    alt={image.display_name}
                    loading="lazy"
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      borderRadius: '6px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />

                  {/* Checkbox de seleccion multiple (esquina superior derecha).
                      Siempre clicable (pointer-events), visualmente aparece al hover
                      o cuando hay seleccion activa para no obstruir la miniatura. */}
                  <button
                    type="button"
                    onClick={(e) => toggleSelect(e, image.id)}
                    className={`absolute z-[160] flex items-center justify-center rounded transition-opacity ${
                      isSelected
                        ? 'bg-red-500 text-white opacity-100'
                        : hasSelection
                          ? 'bg-black/60 text-white/90 opacity-100'
                          : 'bg-black/60 text-white/90 opacity-0 group-hover:opacity-100'
                    }`}
                    style={{ top: '4px', right: '4px', width: '22px', height: '22px' }}
                    data-testid={`gallery-select-${image.id}`}
                    title={language === 'es' ? 'Seleccionar' : 'Select'}
                  >
                    {isSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                  </button>
                  
                  {/* Badge de tipo (esquina superior izquierda ahora — el checkbox ocupa la der) */}
                  <div className="absolute top-1 left-1 pointer-events-none">
                    <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full ${
                      image.type === 'IMAGE_SOURCE' ? 'bg-emerald-500' : 'bg-purple-500'
                    }`}>
                      {image.type === 'IMAGE_SOURCE' && <Image className="w-2 h-2 text-white" />}
                      {image.type === 'IMAGE_VARIANT' && <Layers className="w-2 h-2 text-white" />}
                    </span>
                  </div>
                  
                  {/* Boton de descargar */}
                  <button
                    type="button"
                    onClick={(e) => handleDownloadImage(e, image)}
                    className="absolute z-[150] flex items-center justify-center bg-black/70 hover:bg-blue-600 text-white rounded-full transition-all"
                    style={{ 
                      bottom: '4px', 
                      right: '4px', 
                      width: '20px', 
                      height: '20px',
                      cursor: 'pointer'
                    }}
                    data-testid={`download-image-${image.id}`}
                    title={language === 'es' ? 'Descargar' : 'Download'}
                  >
                    <Download className="w-3 h-3" />
                  </button>
                  
                  {/* Boton de eliminar (X) — ahora en bottom-left para no chocar con el badge */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                      handleDeleteImage(e, image);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    disabled={deletingId === image.id}
                    className="absolute z-[150] flex items-center justify-center bg-black/70 hover:bg-red-600 text-white rounded-full transition-all"
                    style={{ 
                      bottom: '4px', 
                      left: '4px', 
                      width: '20px', 
                      height: '20px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                    data-testid={`delete-image-${image.id}`}
                    title={language === 'es' ? 'Eliminar' : 'Delete'}
                  >
                    {deletingId === image.id ? (
                      <div className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <X className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
                );
              })}
            </div>
            {/* 🟥 Sentinel para infinite scroll. Cuando entra al viewport del
                scroll container → dispara loadMore(). */}
            {hasMore && (
              <div
                ref={sentinelRef}
                data-testid="gallery-infinite-sentinel"
                className="flex items-center justify-center py-4"
              >
                {isLoadingMore && (
                  <div className="flex items-center gap-2 text-sm text-zinc-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {language === 'es' ? 'Cargando más...' : 'Loading more...'}
                  </div>
                )}
              </div>
            )}
            </>
          )}
        </div>
      </div>

      {/* Modal de confirmacion (reemplaza window.confirm que rompia el DOM) */}
      {pendingDeleteImage && (
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={(e) => { e.stopPropagation(); setPendingDeleteImage(null); }}
          data-testid="gallery-delete-confirm"
        >
          <div
            className="bg-zinc-800 border border-zinc-700 rounded-xl p-5 max-w-sm mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-white mb-2">
              {language === 'es' ? '¿Eliminar esta imagen?' : 'Delete this image?'}
            </h3>
            <p className="text-sm text-zinc-400 mb-4">
              {language === 'es'
                ? 'Esta acción no se puede deshacer.'
                : 'This action cannot be undone.'}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setPendingDeleteImage(null)}
                className="px-4 py-2 text-sm text-zinc-300 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                data-testid="cancel-gallery-delete"
              >
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={confirmDeleteImage}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors"
                data-testid="confirm-gallery-delete"
              >
                {language === 'es' ? 'Eliminar' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmacion para borrado MASIVO */}
      {pendingBulkDelete && (
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={(e) => { e.stopPropagation(); if (!isBulkDeleting) setPendingBulkDelete(false); }}
          data-testid="gallery-bulk-delete-confirm"
        >
          <div
            className="bg-zinc-800 border border-zinc-700 rounded-xl p-5 max-w-sm mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-white mb-2">
              {language === 'es'
                ? `¿Eliminar ${selectedIds.size} imagen(es) seleccionada(s)?`
                : `Delete ${selectedIds.size} selected image(s)?`}
            </h3>
            <p className="text-sm text-zinc-400 mb-4">
              {language === 'es'
                ? 'Esta accion no se puede deshacer. Las imagenes se eliminaran del Area de Trabajo.'
                : 'This action cannot be undone. Images will be removed from the Workspace.'}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={(e) => { e.stopPropagation(); setPendingBulkDelete(false); }}
                disabled={isBulkDeleting}
                className="px-4 py-2 rounded-lg text-sm text-zinc-300 hover:bg-zinc-700"
                data-testid="gallery-bulk-delete-cancel"
              >
                {language === 'es' ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); confirmBulkDelete(); }}
                disabled={isBulkDeleting}
                className="px-4 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-500 text-white font-semibold flex items-center gap-2"
                data-testid="gallery-bulk-delete-confirm-btn"
              >
                {isBulkDeleting && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {language === 'es' ? 'Eliminar' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  
  // Usar portal para renderizar en el body
  return createPortal(modalContent, document.body);
};

export default ImageGalleryModal;
